import * as Sentry from '@sentry/react';
import { useState, useRef, useEffect } from 'react';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { AppError } from '@/lib/errors';
import '@uppy/core/css/style.css';
import supabase from '@/utils/supabase';
import { Button } from '@/components/ui/button';
import { getAuthHeaders } from '@/utils/auth';
import {
  Dialog,
  DialogTrigger,
  DialogTitle,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import type { Database } from '@/types/database.types';
type Project = Database['public']['Tables']['projects']['Row'];
type Deliverable = Database['public']['Tables']['deliverables']['Row'];
import type { UppyFile, Meta, Body } from '@uppy/core';

type UploadErrorResponse = {
  status?: number;
  body?: unknown;
  bytesUploaded?: number;
  uploadURL?: string;
};

export default function UploadVersion({
  deliverable,
  project,
}: {
  deliverable: Deliverable;
  project: Project;
}) {
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(false);
  const [uppy] = useState(() => {
    const instance = new Uppy();
    instance.use(AwsS3, {
      shouldUseMultipart: true,
      async createMultipartUpload(file) {
        const response = await fetch(
          `/api/team/projects/${project.id}/deliverables/${deliverable.id}/upload/create-multipart`,
          {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({
              versionId: file.meta.versionId,
              filename: file.meta.displayName,
              contentType: file.type,
              fileSize: file.size,
            }),
          }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to create multipart upload',
            response.status,
            'FAILED_CREATE_MULTIPART_UPLOAD'
          );
        }
        instance.setFileMeta(file.id, { dbUploadId: data.uploadId });
        return {
          uploadId: data.s3UploadId,
          key: data.key,
        };
      },
      async signPart(_file, { uploadId, key, partNumber, signal }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const params = new URLSearchParams({
          s3UploadId: uploadId,
          key,
          partNumber: String(partNumber),
        });
        const response = await fetch(
          `/api/team/projects/${project.id}/deliverables/${deliverable.id}/upload/sign-part?${params}`,
          { signal, headers: await getAuthHeaders() }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to sign part.',
            response.status,
            'FAILED_SIGN_MULTIPART'
          );
        }
        return { url: data.signedUrl };
      },
      async completeMultipartUpload(file, { uploadId, key, parts }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const response = await fetch(
          `/api/team/projects/${project.id}/deliverables/${deliverable.id}/upload/complete-multipart`,
          {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({
              uploadId: file.meta.dbUploadId,
              s3UploadId: uploadId,
              versionId: file.meta.versionId,
              fileName: file.meta.displayName,
              versionDisplay: file.meta.versionDisplay,
              key,
              parts,
            }),
          }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to complete multipart.',
            response.status,
            'FAILED_COMPLETE_MULTIPART'
          );
        }
        return {};
      },
      async abortMultipartUpload(file, { uploadId, key }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const response = await fetch(
          `/api/team/projects/${project.id}/deliverables/${deliverable.id}/upload/abort-multipart`,
          {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({
              uploadId: file.meta.dbUploadId,
              s3UploadId: uploadId,
              versionId: file.meta.versionId,
              key,
            }),
          }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to abort multipart.',
            response.status,
            'FAILED_ABORT_MULTIPART'
          );
        }
      },
      async listParts(_file, { uploadId, key }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const params = new URLSearchParams({
          s3UploadId: uploadId,
          key,
        });
        const response = await fetch(
          `/api/team/projects/${project.id}/deliverables/${deliverable.id}/upload/list-parts?${params}`,
          { headers: await getAuthHeaders() }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to list parts.',
            response.status,
            'FAILED_LIST_MULTIPART_PARTS'
          );
        }
        return data.parts;
      },
    });
    return instance;
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (selected && !selected.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file.');
      return;
    }
    if (!notes.trim()) {
      toast.error('Please leave a note about the edit.');
      return;
    }
    setIsUploading(true);
    try {
      const { data: latestPromoted, error: promotedError } = await supabase
        .from('versions')
        .select('client_version')
        .eq('deliverable_id', deliverable.id)
        .eq('is_internal', false)
        .order('client_version', { ascending: false })
        .limit(1)
        .single();
      if (promotedError && promotedError.code !== 'PGRST116') {
        throw promotedError;
      }
      const currentClientVersion = latestPromoted?.client_version ?? 0;

      const { data: latestTeam, error: teamError } = await supabase
        .from('versions')
        .select('team_version')
        .eq('deliverable_id', deliverable.id)
        .eq('client_version', currentClientVersion)
        .order('team_version', { ascending: false })
        .limit(1)
        .single();
      if (teamError && teamError.code !== 'PGRST116') {
        throw teamError;
      }
      const newTeamVersion = (latestTeam?.team_version ?? 0) + 1;
      const versionDisplay = `${currentClientVersion}.${newTeamVersion}`;

      const { data: version, error: versionError } = await supabase
        .from('versions')
        .insert({
          deliverable_id: deliverable.id,
          client_version: currentClientVersion,
          team_version: newTeamVersion,
          version_display: versionDisplay,
          notes: notes.trim(),
          is_internal: true,
        })
        .select()
        .single();

      if (versionError) {
        if (versionError?.code === '23505') {
          toast.error('Version already exists — please try again.', {
            duration: Infinity,
            closeButton: true,
          });
          return;
        }
        throw versionError;
      }
      const ext = file.name.substring(file.name.lastIndexOf('.'));
      const displayName = `${deliverable.deliverable_name} V${versionDisplay}${ext}`;
      uppy.addFile({
        name: displayName,
        type: file.type,
        data: file,
        meta: {
          versionId: version.id,
          displayName,
          versionDisplay,
        },
      });
      await uppy.upload();
    } catch (error) {
      setIsUploading(false);
      toast.error(
        error instanceof Error ? error.message : 'Failed to start upload',
        { duration: Infinity, closeButton: true }
      );
    }
  };

  useEffect(() => {
    const onProgress = (p: number) => setProgress(p);
    uppy.on('progress', onProgress);
    return () => {
      uppy.off('progress', onProgress);
    };
  }, [uppy]);

  useEffect(() => {
    const onComplete = () => {
      setIsUploading(false);
      toast.success('Video uploaded successfully');
      setOpen(false);
      setTimeout(() => {
        setNotes('');
        setFile(null);
        setProgress(0);
      }, 300);
    };

    uppy.on('complete', onComplete);
    return () => {
      uppy.off('complete', onComplete);
    };
  }, [uppy]);

  const cleanupFailedUpload = (versionId: string, dbUploadId?: string) => {
    const runCleanup = async () => {
      const response = await fetch(
        `/api/team/projects/${project.id}/deliverables/${deliverable.id}/upload/abort-multipart`,
        {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({
            versionId,
            uploadId: dbUploadId,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to cleanup upload: ${response.status}`);
      }
    };

    runCleanup().catch((error) => {
      console.error('Cleanup failed upload error:', error);
      Sentry.captureException(error, {
        tags: { component: 'MediaUploader', kind: 'cleanup-failure' },
        extra: { versionId, dbUploadId },
      });
    });
  };

  useEffect(() => {
    const onCancelAll = () => {
      setIsUploading(false);
      toast.info('Upload cancelled', { duration: Infinity, closeButton: true });
      const fileMeta = uppy.getFiles()[0]?.meta;
      if (fileMeta?.versionId) {
        cleanupFailedUpload(
          fileMeta.versionId as string,
          fileMeta.dbUploadId as string | undefined
        );
      }
    };
    uppy.on('cancel-all', onCancelAll);
    return () => {
      uppy.off('cancel-all', onCancelAll);
    };
  }, [uppy]);

  useEffect(() => {
    const onError = (error: Error) => {
      Sentry.captureException(error, { tags: { component: 'MediaUploader' } });
    };
    const onUploadError = (
      file: UppyFile<Meta, Body> | undefined,
      error: Error,
      response?: UploadErrorResponse
    ) => {
      Sentry.captureException(error, {
        tags: { component: 'MediaUploader', kind: 'upload-error' },
        extra: {
          fileName: file?.name,
          status: response?.status,
          body: response?.body,
        },
      });
      if (file?.meta?.versionId) {
        cleanupFailedUpload(
          file.meta.versionId as string,
          file.meta.dbUploadId as string | undefined
        );
      }
    };

    uppy.on('error', onError);
    uppy.on('upload-error', onUploadError);

    return () => {
      uppy.off('error', onError);
      uppy.off('upload-error', onUploadError);
    };
  }, [uppy]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (isUploading) return;
        setOpen(value);
        if (!value && !isUploading) {
          setTimeout(() => {
            setNotes('');
            setFile(null);
            setProgress(0);
          }, 300);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant={'outline'} className="cursor-pointer">
          Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl!">
        <DialogHeader>
          <DialogTitle>Upload new version</DialogTitle>
          <DialogDescription>
            Leave a note with updates or challenges for the producer:
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
            accept="video/*"
          />
          <Textarea
            value={notes}
            disabled={isUploading}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-50"
          />
          {isUploading && (
            <div className="w-full bg-muted rounded h-2">
              <div
                className="bg-primary h-2 rounded transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              disabled={isUploading}
              variant={'outline'}
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              Select video
            </Button>
            {file && (
              <span className="text-sm text-muted-foreground">{file.name}</span>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            className="cursor-pointer"
            variant={'outline'}
            onClick={() => {
              if (isUploading) {
                uppy.cancelAll();
              } else {
                setOpen(false);
              }
            }}
          >
            {isUploading ? 'Cancel' : 'Close'}
          </Button>
          <Button
            className="cursor-pointer"
            disabled={!notes.trim() || !file || isUploading}
            onClick={handleUpload}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
